import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Layers, X, Info, ExternalLink, Globe } from "lucide-react";
import Modal from "@/components/Modal";

type Project = {
  id: string;
  title: string;
  description: string;
  imageUrl: string | null;
  videoUrl: string | null;
  category: string;
  featured: boolean;
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 30 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const PortfolioGrid = ({ projects }: { projects: Project[] }) => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  if (projects.length === 0) {
    return (
      <div className="text-center py-20">
        <Layers size={48} className="text-white/10 mx-auto mb-4" />
        <p className="text-text-muted text-lg">No projects available yet. Check back soon.</p>
      </div>
    );
  }

  const getEmbedUrl = (url: string) => {
    if (!url) return null;

    // YouTube / Shorts
    const ytRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/i;
    const ytMatch = url.match(ytRegex);
    if (ytMatch && ytMatch[1]) {
      const isShort = url.toLowerCase().includes('shorts');
      return { 
        type: "youtube", 
        url: `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0&modestbranding=1`,
        isVertical: isShort 
      };
    }

    // Vimeo
    const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/i;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch && vimeoMatch[1]) {
      return { type: "vimeo", url: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&badge=0&autopause=0`, isVertical: false };
    }

    // Instagram / Reels
    const instaRegex = /(?:instagram\.com\/(?:p|reels|reel)\/)([^/?#&]+)/i;
    const instaMatch = url.match(instaRegex);
    if (instaMatch && instaMatch[1]) {
      const isReel = url.toLowerCase().includes('reels') || url.toLowerCase().includes('reel');
      return { 
        type: "instagram", 
        url: `https://www.instagram.com/p/${instaMatch[1]}/embed`,
        isVertical: isReel
      };
    }

    // Direct Video File
    if (url.match(/\.(mp4|webm|ogg)$/i)) {
      return { type: "video", url, isVertical: false };
    }

    // Website / External Link
    if (url.startsWith("http")) {
      return { type: "website", url, isVertical: false };
    }

    return null;
  };

  return (
    <>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
      >
        {projects.map((project) => {
          const embed = getEmbedUrl(project.videoUrl || "");
          return (
            <motion.div
              key={project.id}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              onClick={() => setSelectedProject(project)}
              className="aspect-video bg-surface rounded-2xl overflow-hidden relative group cursor-pointer border border-white/5 shadow-2xl"
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-80 group-hover:opacity-40 transition-opacity duration-500 z-10" />

              {/* Action Icon on Hover */}
              {embed && (
                <div className="absolute inset-0 flex items-center justify-center z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-16 h-16 rounded-full bg-primary/20 backdrop-blur-md border border-primary/30 flex items-center justify-center text-white scale-75 group-hover:scale-100 transition-transform duration-500">
                    {embed.type === "website" ? <Globe size={32} /> : <Play size={32} fill="white" />}
                  </div>
                </div>
              )}

              {/* Project Image */}
              {project.imageUrl ? (
                <img
                  src={project.imageUrl}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-in-out"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/10 group-hover:scale-110 transition-transform duration-700 ease-in-out" />
              )}

              {/* Project Info */}
              <div className="absolute bottom-0 left-0 w-full p-8 z-20 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <span className="text-xs font-bold text-primary tracking-widest uppercase mb-2 block opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                  {project.category}
                </span>
                <h3 className="text-2xl font-bold text-white mb-2">{project.title}</h3>
                <p className="text-sm text-text-muted mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150 line-clamp-2">
                  {project.description}
                </p>
                <div className="flex items-center gap-2 text-sm text-text-muted opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-200">
                  <span className="w-8 h-px bg-primary" />
                  {embed?.type === "website" ? "Visit Website" : embed ? "Watch Video" : "View Details"}
                </div>
              </div>

              {/* Border glow on hover */}
              <div className="absolute inset-0 border-2 border-primary/0 group-hover:border-primary/50 transition-all duration-500 rounded-2xl z-30 pointer-events-none" />
            </motion.div>
          );
        })}
      </motion.div>

      {/* Project Detail Modal / Overlay */}
      <AnimatePresence>
        {selectedProject && (() => {
          const embed = getEmbedUrl(selectedProject.videoUrl || "");
          return (
            <Modal onClose={() => setSelectedProject(null)} maxWidth={embed?.isVertical ? "max-w-4xl" : "max-w-6xl"}>
              <div className="relative">
                <button 
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 z-50 p-2 bg-black/50 backdrop-blur-md rounded-full text-white hover:bg-red-500 transition-colors"
                >
                  <X size={24} />
                </button>

                <div className={`flex flex-col ${embed?.isVertical ? 'lg:flex-row items-center lg:items-stretch' : 'lg:flex-row'}`}>
                  {/* Media Container */}
                  <div className={`${embed?.isVertical ? 'lg:w-[50%] aspect-[9/16] max-h-[85vh]' : 'lg:w-[70%] aspect-video'} bg-black flex items-center justify-center overflow-hidden relative group`}>
                    {embed ? (
                      embed.type === "youtube" || embed.type === "vimeo" ? (
                        <iframe
                          src={embed.url}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                          frameBorder="0"
                        />
                      ) : embed.type === "instagram" ? (
                        <iframe
                          src={embed.url}
                          className="w-full h-full min-h-[500px]"
                          allowFullScreen
                          frameBorder="0"
                        />
                      ) : embed.type === "video" ? (
                        <video
                          src={embed.url}
                          controls
                          autoPlay
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        /* Website Type — show preview + button to open */
                        <div className="w-full h-full flex flex-col items-center justify-center p-12 bg-surface">
                          {selectedProject.imageUrl && (
                            <div className="relative w-full h-48 mb-8 rounded-xl overflow-hidden border border-white/10 opacity-50">
                              <img src={selectedProject.imageUrl} alt={selectedProject.title} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <Globe size={48} className="text-primary mb-6" />
                          <h3 className="text-2xl font-bold text-white mb-4">Live Website Project</h3>
                          <p className="text-text-muted text-center max-w-md mb-8">
                            This project is a live website. Click below to view it in a new tab.
                          </p>
                          <a 
                            href={embed.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn-primary flex items-center gap-2"
                          >
                            Visit Live Site <ExternalLink size={18} />
                          </a>
                        </div>
                      )
                    ) : selectedProject.imageUrl ? (
                      <div className="relative w-full h-full">
                        <img src={selectedProject.imageUrl} alt={selectedProject.title} className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/10 flex items-center justify-center">
                        <Layers size={64} className="text-primary/20" />
                      </div>
                    )}
                  </div>

                  {/* Details Container */}
                  <div className={`${embed?.isVertical ? 'lg:w-[50%]' : 'lg:w-[30%]'} p-8 md:p-10 border-t lg:border-t-0 lg:border-l border-white/10 bg-[#0c0c0e] flex flex-col`}>
                    <div className="flex-1">
                      <span className="inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] font-bold uppercase tracking-widest mb-6">
                        {selectedProject.category}
                      </span>
                      <h2 className="text-3xl font-black text-white mb-6 leading-tight">
                        {selectedProject.title}
                      </h2>
                      <div className="space-y-6">
                        <div>
                          <h4 className="flex items-center gap-2 text-sm font-bold text-white/50 uppercase tracking-widest mb-3">
                            <span className="flex items-center gap-2"><Info size={14} /> Description</span>
                          </h4>
                          <p className="text-text-muted leading-relaxed text-sm">
                            {selectedProject.description}
                          </p>
                        </div>
                        
                        {embed?.type === "website" && (
                          <div className="pt-6 border-t border-white/5">
                             <a 
                              href={embed.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-white font-bold hover:opacity-90 transition-opacity"
                            >
                              Go to Website <ExternalLink size={16} />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="pt-8 border-t border-white/5">
                      <button 
                        onClick={() => setSelectedProject(null)}
                        className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white/50 font-bold hover:bg-white/10 hover:text-white transition-all"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          );
        })()}
      </AnimatePresence>
    </>
  );
};

export default PortfolioGrid;
